import { UUIHorizontalShakeKeyframes, UUIHorizontalShakeAnimationValue } from '@umbraco-ui/uui-base/lib/animations';
import { defineElement } from '@umbraco-ui/uui-base/lib/registration';
import { css, LitElement, html } from 'lit';
import { query, property } from 'lit/decorators.js';
import { UUIEvent } from '@umbraco-ui/uui-base/lib/events';
import { FormControlMixin } from '@umbraco-ui/uui-base/lib/mixins';

class UUIRadioEvent extends UUIEvent {
}
UUIRadioEvent.CHANGE = "change";

var __defProp$1 = Object.defineProperty;
var __getOwnPropDesc$1 = Object.getOwnPropertyDescriptor;
var __decorateClass$1 = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc$1(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp$1(target, key, result);
  return result;
};
let UUIRadioElement = class extends LitElement {
  constructor() {
    super();
    this.name = "";
    this.value = "";
    this.label = "";
    this._checked = false;
    this._disabled = false;
    this.addEventListener("mousedown", () => {
      this.style.setProperty("--uui-show-focus-outline", "0");
    });
    this.addEventListener("blur", () => {
      this.style.setProperty("--uui-show-focus-outline", "1");
    });
  }
  get checked() {
    return this._checked;
  }
  set checked(value) {
    const oldValue = this._checked;
    this._checked = value;
    if (value === true) {
      this.setAttribute("aria-checked", "");
      if (!this.disabled) {
        this.setAttribute("tabindex", "0");
      }
    } else {
      this.setAttribute("tabindex", "-1");
      this.removeAttribute("aria-checked");
    }
    this.requestUpdate("checked", oldValue);
  }
  get disabled() {
    return this._disabled;
  }
  set disabled(newVal) {
    const oldVal = this._disabled;
    this._disabled = newVal;
    this.setAttribute("aria-hidden", newVal ? "true" : "false");
    this.setAttribute("tabindex", newVal ? "-1" : "0");
    this.requestUpdate("disabled", oldVal);
  }
  focus() {
    this.inputElement.focus();
  }
  click() {
    this.inputElement.click();
  }
  _onChange(e) {
    e.stopPropagation();
    const checked = this.inputElement.checked;
    this.checked = checked;
    if (checked) {
      this.focus();
    }
    this.dispatchEvent(new UUIRadioEvent(UUIRadioEvent.CHANGE));
  }
  uncheck() {
    this.checked = false;
  }
  check() {
    this.checked = true;
  }
  makeFocusable() {
    if (!this.disabled) {
      this.setAttribute("tabindex", "0");
    }
  }
  makeUnfocusable() {
    if (!this.disabled) {
      this.setAttribute("tabindex", "-1");
    }
  }
  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute("tabindex"))
      this.setAttribute("tabindex", "-1");
    if (!this.hasAttribute("aria-checked"))
      this.removeAttribute("aria-checked");
  }
  render() {
    return html` <label>
      <input
        id="input"
        type="radio"
        name=${this.name}
        value=${this.value}
        .checked=${this.checked}
        .disabled=${this.disabled}
        @change=${this._onChange} />
      <div id="button"></div>
      <div id="label">
        ${this.label ? html`<span>${this.label}</span>` : html`<slot></slot>`}
      </div>
    </label>`;
  }
};
UUIRadioElement.styles = [
  UUIHorizontalShakeKeyframes,
  css`
      :host {
        display: block;
        box-sizing: border-box;
        font-family: inherit;
        color: currentColor;
        --uui-radio-button-size: var(--uui-size-6,18px);
        margin: var(--uui-size-2,6px) 0;
      }

      label {
        display: block;
        box-sizing: border-box;
        display: flex;
        align-items: center;
        cursor: pointer;
        line-height: 18px;
      }

      #input {
        width: 0;
        height: 0;
        opacity: 0;
        margin: 0;
      }

      .label {
        margin-top: 2px;
      }

      #button {
        box-sizing: border-box;
        display: inline-block;
        width: var(--uui-radio-button-size, 18px);
        height: var(--uui-radio-button-size, 18px);
        background-color: var(--uui-color-surface,#fff);
        border: 1px solid var(--uui-color-border-standalone,#c2c2c2);
        border-radius: 100%;
        margin-right: calc(var(--uui-size-2,6px) * 2);
        position: relative;
        flex: 0 0 var(--uui-radio-button-size);
      }

      #button::after {
        content: '';
        width: calc(var(--uui-radio-button-size) / 2);
        height: calc(var(--uui-radio-button-size) / 2);
        background-color: var(--uui-color-selected,#3544b1);
        border-radius: 100%;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) scale(0);
        transition: all 0.15s ease-in-out;
      }

      :host(:hover) #button {
        border: 1px solid var(--uui-color-border-emphasis,#a1a1a1);
      }

      :host(:focus) {
        outline: none;
      }
      :host(:focus) #button {
        outline: calc(2px * var(--uui-show-focus-outline, 1)) solid
          var(--uui-color-focus,#3879ff);
      }

      input:checked ~ #button::after {
        transform: translate(-50%, -50%) scale(1);
      }

      input:checked ~ #button {
        border: 1px solid var(--uui-color-selected,#3544b1);
      }

      input:checked:hover ~ #button {
        border: 1px solid var(--uui-color-selected-emphasis,rgb(70, 86, 200));
      }

      input:checked:hover ~ #button::after {
        background-color: var(--uui-color-selected-emphasis,rgb(70, 86, 200));
      }

      :host([disabled]) label {
        cursor: default;
        opacity: 0.5;
      }
      :host([disabled]) .label {
        color: var(--uui-color-disabled-contrast,#c4c4c4);
      }

      :host([disabled]) input ~ #button {
        border: 1px solid var(--uui-color-disabled-contrast,#c4c4c4);
      }

      :host([disabled]) input:checked ~ #button {
        border: 1px solid var(--uui-color-disabled-contrast,#c4c4c4);
      }

      :host([disabled]) input:checked ~ #button::after {
        background-color: var(--uui-color-disabled-contrast,#c4c4c4);
      }

      :host([disabled]:active) #button {
        animation: ${UUIHorizontalShakeAnimationValue};
      }

      @media (prefers-reduced-motion) {
        :host([disabled]:active) #button {
          animation: none;
        }

        #button::after {
          transition: none;
        }
      }
    `
];
__decorateClass$1([
  query("#input")
], UUIRadioElement.prototype, "inputElement", 2);
__decorateClass$1([
  property({ type: String })
], UUIRadioElement.prototype, "name", 2);
__decorateClass$1([
  property({ type: String })
], UUIRadioElement.prototype, "value", 2);
__decorateClass$1([
  property({ type: String })
], UUIRadioElement.prototype, "label", 2);
__decorateClass$1([
  property({ type: Boolean, reflect: true })
], UUIRadioElement.prototype, "checked", 1);
__decorateClass$1([
  property({ type: Boolean, reflect: true })
], UUIRadioElement.prototype, "disabled", 1);
UUIRadioElement = __decorateClass$1([
  defineElement("uui-radio")
], UUIRadioElement);

class UUIRadioGroupEvent extends UUIEvent {
}
UUIRadioGroupEvent.CHANGE = "change";

var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
const ARROW_LEFT = "ArrowLeft";
const ARROW_UP = "ArrowUp";
const ARROW_RIGHT = "ArrowRight";
const ARROW_DOWN = "ArrowDown";
const SPACE = " ";
let UUIRadioGroupElement = class extends FormControlMixin(LitElement) {
  constructor() {
    super();
    this.disabled = false;
    this._selected = null;
    this._onChildBlur = () => {
      this.pristine = false;
    };
    this._handleSelectOnClick = (e) => {
      e.stopPropagation();
      if (e.target.checked === true) {
        this.value = e.target.value;
        this._fireChangeEvent();
      }
    };
    this.addEventListener("keydown", this._onKeydown);
    this.addEventListener("keypress", this._onKeypress);
  }
  get value() {
    return this._value;
  }
  set value(newValue) {
    super.value = newValue;
    if (newValue === null || newValue === "") {
      this._makeFirstEnabledFocusable();
    }
    this._radioElements.forEach((el, index) => {
      if (el.value === newValue) {
        el.checked = true;
        this._selected = index;
      } else {
        el.checked = false;
      }
    });
  }
  focus() {
    var _a, _b;
    if (this._selected !== null) {
      (_a = this._radioElements[this._selected]) == null ? void 0 : _a.focus();
    } else {
      (_b = this._findNextEnabledElement()) == null ? void 0 : _b.focus();
    }
  }
  click() {
    var _a, _b;
    if (this._selected !== null) {
      (_a = this._radioElements[this._selected]) == null ? void 0 : _a.click();
    } else {
      (_b = this._findNextEnabledElement()) == null ? void 0 : _b.click();
    }
  }
  getFormElement() {
    if (this._radioElements && this._selected) {
      return this._radioElements[this._selected];
    }
    return void 0;
  }
  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute("role"))
      this.setAttribute("role", "radiogroup");
  }
  _setNameOnRadios(name) {
    var _a;
    (_a = this._radioElements) == null ? void 0 : _a.forEach((el) => el.name = name);
  }
  _setDisableOnRadios(value) {
    var _a;
    (_a = this._radioElements) == null ? void 0 : _a.forEach((el) => el.disabled = value);
  }
  _handleSlotChange(e) {
    var _a;
    e.stopPropagation();
    (_a = this._radioElements) == null ? void 0 : _a.forEach((el) => {
      el.removeEventListener(
        UUIRadioEvent.CHANGE,
        this._handleSelectOnClick
      );
      el.removeEventListener("blur", this._onChildBlur);
    });
    this._selected = null;
    this._radioElements = e.target.assignedElements({ flatten: true }).filter((el) => el instanceof UUIRadioElement);
    if (this._radioElements.length > 0) {
      this._radioElements.forEach((el) => {
        el.addEventListener(
          UUIRadioEvent.CHANGE,
          this._handleSelectOnClick
        );
        el.addEventListener("blur", this._onChildBlur);
      });
      this._setNameOnRadios(this.name);
      if (this.disabled) {
        this._setDisableOnRadios(true);
      }
      const checkedRadios = this._radioElements.filter(
        (el) => el.checked === true
      );
      if (checkedRadios.length > 1) {
        this._radioElements.forEach((el) => {
          el.checked = false;
        });
        this.value = "";
        console.error(
          "There can only be one checked radio among the <" + this.nodeName + "> children",
          this
        );
      }
      if (checkedRadios.length === 1) {
        this.value = checkedRadios[0].value;
        this._selected = this._radioElements.indexOf(checkedRadios[0]);
        if (checkedRadios[0].disabled === false) {
          this._radioElements.forEach((el) => {
            el.makeUnfocusable();
          });
          checkedRadios[0].makeFocusable();
        } else {
          this._makeFirstEnabledFocusable();
        }
      } else {
        this._makeFirstEnabledFocusable();
      }
    }
  }
  _makeFirstEnabledFocusable() {
    var _a, _b;
    this._selected = null;
    (_a = this._radioElements) == null ? void 0 : _a.forEach((el) => {
      el.makeUnfocusable();
    });
    (_b = this._findNextEnabledElement()) == null ? void 0 : _b.makeFocusable();
  }
  _findNextEnabledElement(direction = 1) {
    if (!this._radioElements) {
      return null;
    }
    const origin = this._selected || 0;
    const len = this._radioElements.length;
    let i = this._selected === null ? 0 : 1;
    while (i < len) {
      let checkIndex = (origin + i * direction) % len;
      if (checkIndex < 0) {
        checkIndex += len;
      }
      if (this._radioElements[checkIndex].disabled === false) {
        return this._radioElements[checkIndex];
      }
      i++;
    }
    return null;
  }
  _selectPreviousElement() {
    var _a, _b;
    this.value = ((_a = this._findNextEnabledElement(-1)) == null ? void 0 : _a.value) || "";
    (_b = this._radioElements[this._selected || 0]) == null ? void 0 : _b.focus();
    this._fireChangeEvent();
  }
  _selectNextElement() {
    var _a, _b;
    this.value = ((_a = this._findNextEnabledElement()) == null ? void 0 : _a.value) || "";
    (_b = this._radioElements[this._selected || 0]) == null ? void 0 : _b.focus();
    this._fireChangeEvent();
  }
  _onKeydown(e) {
    var _a;
    e.stopPropagation();
    switch (e.key) {
      case ARROW_LEFT:
      case ARROW_UP: {
        e.preventDefault();
        this._selectPreviousElement();
        break;
      }
      case ARROW_RIGHT:
      case ARROW_DOWN: {
        e.preventDefault();
        this._selectNextElement();
        break;
      }
      case SPACE: {
        if (this._selected === null) {
          this.value = (_a = this._findNextEnabledElement()) == null ? void 0 : _a.value;
          this._fireChangeEvent();
        }
      }
    }
  }
  _onKeypress(e) {
    if (e.key == "Enter") {
      this.submit();
    }
  }
  _fireChangeEvent() {
    this.pristine = false;
    this.dispatchEvent(new UUIRadioGroupEvent(UUIRadioGroupEvent.CHANGE));
  }
  updated(_changedProperties) {
    super.updated(_changedProperties);
    if (_changedProperties.has("disabled")) {
      this._setDisableOnRadios(this.disabled);
    }
    if (_changedProperties.has("name")) {
      this._setNameOnRadios(_changedProperties.get("name"));
    }
  }
  render() {
    return html` <slot @slotchange=${this._handleSlotChange}></slot> `;
  }
};
UUIRadioGroupElement.formAssociated = true;
UUIRadioGroupElement.styles = [
  css`
      :host {
        display: inline-block;
        padding-right: 3px;
        border: 1px solid transparent;
        border-radius: var(--uui-border-radius,3px);
      }

      :host(:not([pristine]):invalid),
      /* polyfill support */
      :host(:not([pristine])[internals-invalid]) {
        border: 1px solid var(--uui-color-danger-standalone,rgb(191, 33, 78));
      }
    `
];
__decorateClass([
  property({ type: Boolean, reflect: true })
], UUIRadioGroupElement.prototype, "disabled", 2);
UUIRadioGroupElement = __decorateClass([
  defineElement("uui-radio-group")
], UUIRadioGroupElement);

export { UUIRadioElement, UUIRadioEvent, UUIRadioGroupElement, UUIRadioGroupEvent };
